import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IncentiveZoneRecordView } from '@vello/api-client';
import { COCKPIT_TABS, groupIncentiveZones, incentiveCockpitAuthority, type CockpitTab } from '../../src/index';
import { useAdmin } from '../session';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { EmptyState, PageHeader, Panel, Stack, TabPanel, Tabs } from '@vello/web-theme';
import { ActiveOffersPanel } from './cockpit/ActiveOffersPanel';
import { ResultsPanel } from './cockpit/ResultsPanel';
import { RulesPanel, type RulesDrafts } from './cockpit/RulesPanel';
import { SpendPanel } from './cockpit/SpendPanel';
import { SurgeRetentionPanel } from './cockpit/SurgeRetentionPanel';
import { ZonesPanel } from './cockpit/ZonesPanel';
import { IncentiveControllerPanels } from './IncentiveControllerScreen';

/**
 * Finance › Cockpit de incentivos (W10-INCENTIVE-COCKPIT, D497, ADR-0117; doc 17 sections 3.4-3.7): the one place an operator commands the incentive engine of the market in the header scope
 * (never typed here). Four tabs:
 *
 *   Operação  - the money (each envelope's balance, what was committed today against the controller's pacing plan, the day's spend curve), the offers in force (the operator's and the controller's,
 *               with a source chip and the time left), and the controller itself: mode and policy, the KILL SWITCH per scope and lever (one operator stops it, four eyes switch it back on) and the
 *               live decision log with its filters.
 *   Zonas     - the zone map coloured by the state of each zone's newest decision, with what the controller did and the offers in force in it.
 *   Resultados- what each lever returned (the measurement, D496) and where the automatic shutdown stands, and "if it were active yesterday".
 *   Regras    - the rule editor: a new `incentives.controllerPolicy` version PROPOSED through the existing governed-settings four-eyes flow, with a read-only preview over yesterday's windows.
 * Every code shown is read through the central vocabulary; a refused or failed read is an ERROR, never an empty panel; no native dialog is ever opened.
 */
export function IncentiveCockpitScreen(): ReactElement {
  const { runtime, grants } = useAdmin();
  const { t } = useT();
  const { mayView } = incentiveCockpitAuthority(grants);
  const { market: scopeMarket, choices } = useScopeMarket();
  const market = scopeMarket ?? '';
  const [tab, setTab] = useState<CockpitTab>('operation');
  const [zoneRecords, setZoneRecords] = useState<readonly IncentiveZoneRecordView[]>([]);
  const [zonesError, setZonesError] = useState<string | undefined>(undefined);
  const { fail } = useVocab();
  const seq = useRef(0);
  const drafts = useRef<RulesDrafts>(new Map());

  const loadZones = useCallback(async (): Promise<void> => {
    const mine = ++seq.current;
    if (market === '' || !mayView) {
      setZoneRecords([]);
      return;
    }
    const res = await runtime.ops.incentiveZones(market);
    if (mine !== seq.current) return;
    setZoneRecords(res.ok ? res.value.zones : []);
    setZonesError(res.ok ? undefined : fail(res.error));
  }, [runtime, market, mayView, fail]);
  useEffect(() => {
    void loadZones();
  }, [loadZones]);

  const zoneNames = useMemo(
    () => new Map(groupIncentiveZones(zoneRecords).filter((z) => z.approved !== undefined).map((z) => [z.zoneId, (z.approved ?? z.latest).definition.name])),
    [zoneRecords],
  );
  const labels: Readonly<Record<CockpitTab, string>> = { operation: t('cpt.tab.operation'), zones: t('cpt.tab.zones'), results: t('cpt.tab.results'), rules: t('cpt.tab.rules') };

  return (
    <>
      <PageHeader title={t('cpt.title')} subtitle={t('cpt.subtitle')} />
      {!mayView ? (
        <Panel><EmptyState title={t('ctl.noPermission')} /></Panel>
      ) : (
        <>
          {scopeMarket === undefined && <ScopeMarketNotice choices={choices} />}
          {scopeMarket !== undefined && (
            <>
              <Tabs id="cpt" ariaLabel={t('cpt.tabs')} value={tab} onChange={setTab} tabs={COCKPIT_TABS.map((id) => ({ id, label: labels[id] }))} />
              <TabPanel id="cpt" tab="operation" value={tab}>
                <Stack gap={4}>
                  <SpendPanel market={market} />
                  <ActiveOffersPanel market={market} zoneNames={zoneNames} />
                  <IncentiveControllerPanels market={market} />
                </Stack>
              </TabPanel>
              <TabPanel id="cpt" tab="zones" value={tab}>
                <Stack gap={4}>
                  <ZonesPanel market={market} zoneRecords={zoneRecords} zonesError={zonesError} />
                  {/* D841 (owner decision D836): the share of the automatic surge increment each zone retains for the fund. */}
                  <SurgeRetentionPanel market={market} />
                </Stack>
              </TabPanel>
              <TabPanel id="cpt" tab="results" value={tab}>
                <ResultsPanel market={market} />
              </TabPanel>
              <TabPanel id="cpt" tab="rules" value={tab}>
                <RulesPanel market={market} drafts={drafts.current} />
              </TabPanel>
            </>
          )}
        </>
      )}
    </>
  );
}
